import { useRef, useState } from 'react';
import { countries, type Country } from './countries';
import { dailyIndex, gameNumber, todayKey } from './game';
import { getModeState, setModeState, recordWin, getStreak } from './storage';
import { celebrate } from './confetti';

export type Status = 'playing' | 'won' | 'lost' | 'revealed';
export const MAX_GUESSES = 6;

export interface GameState {
  day: string;
  num: number;
  answer: Country;
  guesses: Country[];
  status: Status;
  streak: number; // daily:每日连胜;endless:会话连胜
  guess: (c: Country) => void;
  // ===== Unlimited 专用 =====
  endless: boolean;
  solved: number; // 累计解出
  best: number; // 最大连胜
  next: () => void; // 下一局(新随机国家)
  skip: () => void; // 跳过并亮答案(断会话连胜)
}

interface Opts {
  pool?: Country[];
  endless?: boolean;
}

interface EndlessSave {
  answerCca3: string;
  guesses: string[];
  status: Status;
  solved: number;
  streak: number;
  best: number;
}
const eKey = (mode: string) => `geodle:endless:${mode}`;
function loadEndless(mode: string): EndlessSave | null {
  try {
    const v = localStorage.getItem(eKey(mode));
    return v ? (JSON.parse(v) as EndlessSave) : null;
  } catch {
    return null;
  }
}
function saveEndless(mode: string, s: EndlessSave): void {
  try {
    localStorage.setItem(eKey(mode), JSON.stringify(s));
    window.dispatchEvent(new Event('geodle:endless'));
  } catch {
    /* ignore */
  }
}

function shuffledIndices(n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const fromCca3 = (c3: string) => countries.find((c) => c.cca3 === c3);

/** 各模式共用的猜词状态机。daily 默认;endless=true 为 Unlimited(随机出题、不计每日连胜)。 */
export function useGuessGame(mode: string, opts: Opts | Country[] = {}): GameState {
  const o: Opts = Array.isArray(opts) ? { pool: opts } : opts;
  const pool = o.pool ?? countries;
  const endless = !!o.endless;

  const day = todayKey();
  const num = gameNumber(day);
  const queueRef = useRef<number[]>([]);

  const [answer, setAnswer] = useState<Country>(() => {
    if (!endless) return pool[dailyIndex(mode, pool.length, day)];
    const saved = loadEndless(mode);
    const restored = saved?.answerCca3 ? pool.find((c) => c.cca3 === saved.answerCca3) : undefined;
    if (restored) return restored;
    queueRef.current = shuffledIndices(pool.length);
    return pool[queueRef.current.pop() as number];
  });

  const [guesses, setGuesses] = useState<Country[]>(() => {
    const list = endless ? loadEndless(mode)?.guesses : getModeState(mode, day).guesses;
    return (list ?? []).map(fromCca3).filter(Boolean) as Country[];
  });

  const [status, setStatus] = useState<Status>(() => {
    if (!endless) {
      const s = getModeState(mode, day).status;
      return s === 'lost' ? 'playing' : s;
    }
    return loadEndless(mode)?.status ?? 'playing';
  });

  const [streak, setStreak] = useState<number>(() =>
    endless ? (loadEndless(mode)?.streak ?? 0) : getStreak(),
  );
  const [solved, setSolved] = useState<number>(() => (endless ? (loadEndless(mode)?.solved ?? 0) : 0));
  const [best, setBest] = useState<number>(() => (endless ? (loadEndless(mode)?.best ?? 0) : 0));

  function guess(c: Country) {
    if (status !== 'playing') return;
    if (guesses.some((g) => g.cca3 === c.cca3)) return;
    const nextGuesses = [...guesses, c];
    setGuesses(nextGuesses);
    const won = c.cca3 === answer.cca3;
    const ns: Status = won ? 'won' : 'playing';
    setStatus(ns);

    if (!endless) {
      setModeState(mode, day, { guesses: nextGuesses.map((g) => g.cca3), status: ns });
      if (won) {
        setStreak(recordWin(day));
        celebrate();
        window.dispatchEvent(new Event('geodle:progress'));
      }
      return;
    }
    // endless
    let nSolved = solved;
    let nStreak = streak;
    let nBest = best;
    if (won) {
      nSolved = solved + 1;
      nStreak = streak + 1;
      nBest = Math.max(best, nStreak);
      setSolved(nSolved);
      setStreak(nStreak);
      setBest(nBest);
      celebrate();
    }
    saveEndless(mode, {
      answerCca3: answer.cca3,
      guesses: nextGuesses.map((g) => g.cca3),
      status: ns,
      solved: nSolved,
      streak: nStreak,
      best: nBest,
    });
  }

  function drawNext(): Country {
    if (!queueRef.current.length) {
      const q = shuffledIndices(pool.length);
      const curIdx = pool.indexOf(answer);
      if (q.length > 1 && q[q.length - 1] === curIdx) [q[q.length - 1], q[0]] = [q[0], q[q.length - 1]];
      queueRef.current = q;
    }
    return pool[queueRef.current.pop() as number];
  }

  function next() {
    if (!endless) return;
    const a = drawNext();
    setAnswer(a);
    setGuesses([]);
    setStatus('playing');
    saveEndless(mode, { answerCca3: a.cca3, guesses: [], status: 'playing', solved, streak, best });
  }

  function skip() {
    if (!endless || status !== 'playing') return;
    setStatus('revealed');
    setStreak(0);
    saveEndless(mode, {
      answerCca3: answer.cca3,
      guesses: guesses.map((g) => g.cca3),
      status: 'revealed',
      solved,
      streak: 0,
      best,
    });
  }

  return { day, num, answer, guesses, status, streak, guess, endless, solved, best, next, skip };
}
